import React from 'react';
import { Typography, Alert, Table, Card, Statistic, Row, Col } from 'antd';

const { Text } = Typography;

interface MegadockPose {
  rank: number;
  score: number;
  rotation_x: number;
  rotation_y: number;
  rotation_z: number;
  translation_x: number;
  translation_y: number;
  translation_z: number;
}

interface MegadockDockingResults {
  poses: MegadockPose[];
  total_poses: number;
  top_score: number;
}

interface MegadockResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const MegadockResult: React.FC<MegadockResultProps> = ({ 
  taskResult, 
  data, 
  title = "MEGADOCK 对接结果", 
  showTitle = true 
}) => {

  // 提取对接结果数据
  const getDockingResults = (): MegadockDockingResults | null => {
    // 格式1: Profile页面格式 - taskResult.result.results[].result.docking_results
    if (taskResult?.result?.results?.[0]?.result?.docking_results) {
      return taskResult.result.results[0].result.docking_results;
    }
    
    // 格式2: 前端页面格式 - taskResult.docking_results
    if (taskResult?.docking_results) {
      return taskResult.docking_results;
    }
    
    // 格式3: data参数
    if (data?.docking_results) {
      return data.docking_results;
    }
    
    // 格式4: 直接在result中
    if (taskResult?.result?.docking_results) {
      return taskResult.result.docking_results;
    }
    
    return null;
  };

  // 提取PPI分析结果
  const getPPIAnalysis = () => {
    // 格式1: Profile页面格式
    if (taskResult?.result?.results?.[0]?.result?.ppi_analysis) {
      return taskResult.result.results[0].result.ppi_analysis;
    }
    
    // 格式2: 前端页面格式
    if (taskResult?.ppi_analysis) {
      return taskResult.ppi_analysis;
    }
    
    // 格式3: artifacts中
    if (taskResult?.artifacts?.ppi_analysis) {
      return taskResult.artifacts.ppi_analysis;
    }
    
    // 格式4: meta中
    if (taskResult?.meta?.ppi_analysis) {
      return taskResult.meta.ppi_analysis;
    }
    
    return null;
  };

  const dockingResults = getDockingResults();
  const ppiAnalysis = getPPIAnalysis();

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (!dockingResults || !dockingResults.poses || dockingResults.poses.length === 0) {
    return (
      <Alert
        type="info"
        message="无MEGADOCK对接结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  const columns = [
    { title: '排名', dataIndex: 'rank', key: 'rank', width: 80 },
    { 
      title: '评分', 
      dataIndex: 'score', 
      key: 'score', 
      width: 100, 
      render: (score: number) => score.toFixed(2),
      sorter: (a: MegadockPose, b: MegadockPose) => b.score - a.score
    },
    { 
      title: '旋转角 X', 
      dataIndex: 'rotation_x', 
      key: 'rotation_x', 
      width: 100, 
      render: (val: number) => val.toFixed(3) 
    },
    { 
      title: '旋转角 Y', 
      dataIndex: 'rotation_y', 
      key: 'rotation_y', 
      width: 100, 
      render: (val: number) => val.toFixed(3) 
    },
    { 
      title: '旋转角 Z', 
      dataIndex: 'rotation_z', 
      key: 'rotation_z', 
      width: 100, 
      render: (val: number) => val.toFixed(3) 
    },
    { title: '平移 X', dataIndex: 'translation_x', key: 'translation_x', width: 80 },
    { title: '平移 Y', dataIndex: 'translation_y', key: 'translation_y', width: 80 },
    { title: '平移 Z', dataIndex: 'translation_z', key: 'translation_z', width: 80 },
  ];

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({dockingResults.total_poses} 个预测)</Text>
        </div>
      )}

      {/* PPI 分析概览 */}
      {ppiAnalysis && (
        <Card title="PPI 分析概览" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Statistic 
                title="置信度" 
                value={ppiAnalysis.confidence || 0} 
                suffix="%" 
                precision={1}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="PPI 评分" 
                value={ppiAnalysis.ppi_score || 0} 
                precision={3}
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="相互作用强度" 
                value={ppiAnalysis.interaction_strength || 'N/A'} 
              />
            </Col>
            <Col span={6}>
              <Statistic 
                title="预测类别" 
                value={ppiAnalysis.prediction_category || 'N/A'} 
              />
            </Col>
          </Row>
        </Card>
      )}

      {/* 对接结果表格 */}
      <Card title={`对接结果详情`} size="small">
        <div style={{ marginBottom: 16 }}>
          <Text strong>最高评分: </Text>
          <Text>{dockingResults.top_score?.toFixed(2) || 'N/A'}</Text>
        </div>
        
        <Table 
          dataSource={dockingResults.poses.slice(0, 20).map((pose: any, index: number) => ({ 
            ...pose, 
            key: index 
          }))}
          columns={columns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          scroll={{ x: 800 }}
        />
        
        {dockingResults.poses.length > 20 && (
          <div style={{ marginTop: 8, textAlign: 'center' }}>
            <Text type="secondary">显示前20个结果，共{dockingResults.total_poses}个预测</Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MegadockResult;
